import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'

export interface RdoTemplateItem {
  id?: number
  descrizione: string
  quantita: number
  ordine: number
}

export interface RdoTemplate {
  id: number
  nome: string
  parola_breve: string
  attivo: boolean
  tenant_id: number | null
  items: RdoTemplateItem[]
  created_at: string
  updated_at: string
}

export interface RdoTemplateCreate {
  nome: string
  parola_breve: string
  attivo: boolean
  items: Omit<RdoTemplateItem, 'id'>[]
}

export interface RdoTemplateUpdate {
  nome?: string
  parola_breve?: string
  attivo?: boolean
  items?: Omit<RdoTemplateItem, 'id'>[]
}

const BASE = '/rdo-templates'

export const useRdoTemplates = (attivo?: boolean) => {
  return useQuery<RdoTemplate[]>({
    queryKey: ['rdo-templates', attivo],
    queryFn: async () => {
      const params = attivo !== undefined ? `?attivo=${attivo}` : ''
      const res = await axiosInstance.get<RdoTemplate[]>(`${BASE}${params}`)
      return res.data
    },
  })
}

export const useMatchRdoTemplate = (q: string) => {
  return useQuery<RdoTemplate>({
    queryKey: ['rdo-template-match', q],
    queryFn: async () => {
      const res = await axiosInstance.get<RdoTemplate>(`${BASE}/match`, { params: { q } })
      return res.data
    },
    enabled: q.trim().length >= 3,
    staleTime: 60_000,
    retry: false,
  })
}

export const useCreateRdoTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: RdoTemplateCreate) => {
      const res = await axiosInstance.post<RdoTemplate>(BASE, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rdo-templates'] })
      message.success('Template creato')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      message.error(detail || 'Errore nella creazione del template')
    },
  })
}

export const useUpdateRdoTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: RdoTemplateUpdate }) => {
      const res = await axiosInstance.put<RdoTemplate>(`${BASE}/${id}`, data)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rdo-templates'] })
      qc.invalidateQueries({ queryKey: ['rdo-template-match'] })
      message.success('Template aggiornato')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      message.error(detail || 'Errore nell\'aggiornamento del template')
    },
  })
}

export const useDeleteRdoTemplate = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${BASE}/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rdo-templates'] })
      qc.invalidateQueries({ queryKey: ['rdo-template-match'] })
      message.success('Template eliminato')
    },
    onError: () => {
      message.error('Errore nell\'eliminazione del template')
    },
  })
}
